/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useMemo, useState} from "react"
import {academicCourseGradesService, attendanceService, instructionalService} from "services"
import {get, head} from "lodash"
import {formatDecimal, handleError} from "helpers"
import {BaseLoading} from "components"
import {MyCourses, CourseInfo, CourseTab} from "./parts"
import {ProfileScore} from "../Common"
import {Course} from "types/courses"
import {useStores} from "stores/useStores"
import {useModel, useVisible} from "hooks"
import {observer} from "mobx-react"
import {DegreeAuditProgressData} from "types/degreeAudit"
import {EditLdaPopup} from "../Common/ProfileScore/EditLdaPopup"
import {EditSapStatusPopup} from "../Common/ProfileScore/EditSapStatusPopup"
import {EditGpaPopup} from "../Common/ProfileScore/EditGpaPopup"
import {EditAttendancePopup} from "../Common/ProfileScore/EditAttendancePopup"
import {useHistory} from "react-router-dom"

type Props = {
    studentId: number
    courseId: number
    scheduleId: number
    onChangeCourseId: (courseId: number, scheduleId: number) => void
}

const AcademicsStudentCourses = observer((props: Props) => {
    const {courseId, studentId, scheduleId, onChangeCourseId} = props
    const history = useHistory()
    const popupForEditingLda = useVisible()
    const popupForEditingSapStatus = useVisible()
    const popupForEditingGpa = useVisible()
    const popupForEditingAttendance = useVisible()
    const [isLoading, setIsLoading] = useState(false)
    const [gpa, setGpa] = useState(0)
    const [academicStudentStatsId, setAcademicStudentStatsId] = useState()
    const [academicTermStatsId, setAcademicTermStatsId] = useState()
    const [termGpa, setTermGpa] = useState(0)
    const [cgpa, setCgpa] = useState(0)
    const [termSapStatus, setTermSapStatus] = useState()
    const [sapStatus, setSapStatus] = useState()
    const [termSapDate, setTermSapDate] = useState()
    const [sapDate, setSapDate] = useState()
    const model = useModel()
    const [lda, setLda] = useState()
    const [studentGradingTemplateId, setStudentGradingTemplateId] = useState<number>()
    const [courseInfo, setCourseInfo] = useState<Course.InstructionalCourse>()
    const [progressData, setProgressData] = useState<DegreeAuditProgressData>({
        completed: 0,
        distEdHours: 0,
        attempted: 0,
        appliedToDegree: 0,
        degreeRequirement: 0,
        totalTransferApplied: 0,
        makeup: 0
    })

    const studentProfileIds = useMemo(() => [studentId], [studentId])

    const {
        academics: {instructional}
    } = useStores()

    useEffect(() => {
        if (courseId) {
            instructional.getCurrentCourse(courseId)
        }
    }, [instructional, courseId])

    const getProgressData = async (termIds: number[], courseId?: number) => {
        let studentProfileId = studentId
        const response = await attendanceService.getCourseStatistics({
            studentProfileIds: [studentProfileId],
            termIds,
            courseId
        })
        const data = response.data
        const totalHours = data.totalHours ?? 0
        const attempted = data.progress.reduce((total, progress) => (total += progress?.total ?? 0), 0)
        const completed = data.progress.reduce((total, progress) => (total += progress?.completed ?? 0), 0)
        setProgressData({
            distEdHours: data.distEdHours,
            attempted,
            completed,
            appliedToDegree: totalHours
        })
    }

    useEffect(() => {
        if (courseId) {
            instructional.getTodayLessons({nextLessons: true})
            instructional.getAllActionNeededLessonsAttendanceByCourse(courseId, studentProfileIds, [])
            instructional.getAttendanceRatingByCourse(courseId, studentProfileIds, [])
            instructional.getAllLessonsAttendanceByCourse(courseId, [], [])
            instructional.getCourseStudentsAttendanceStats(courseId, [])
            instructional.getMissedAttendanceStats({
                courseIds: [courseId],
                termIds: [],
                studentProfileIds: studentProfileIds
            })
        }
    }, [studentId, courseId])

    useEffect(() => {
        if (courseId) {
            getCourseInfo()
            getAttendanceStats()
        }
    }, [courseId, studentId, scheduleId])

    useEffect(() => {
        if (courseInfo) {
            getGpaAndLdaInfo()
        }
    }, [courseInfo])

    const getCourseInfo = async () => {
        try {
            setIsLoading(true)
            const params: any = {
                range: {page: 1, pageSize: 1},
                filter: {isPracticalOrClinicalServices: false, courseIds: [courseId]}
            }
            if (studentId) {
                params.filter.studentProfileIds = [studentId]
            }
            if (scheduleId) {
                params.filter.scheduleIds = [scheduleId]
            }
            const {data} = await instructionalService.getInstructionalMyCourse(params)
            setCourseInfo(head(data))
        } catch (error) {
            handleError(error)
        } finally {
            setIsLoading(false)
        }
    }

    const CourseInfoComponent = useMemo(() => {
        if (courseInfo) {
            return (
                <CourseInfo
                    gpa={gpa}
                    studentProfileId={studentId}
                    type={courseInfo?.type}
                    isAcademicStudentTerm
                    termInfo={null}
                    codeCourse={courseInfo?.courseCode}
                    courseName={courseInfo?.courseName}
                    instructor={courseInfo?.instructors}
                    firstDayOfCourse={courseInfo?.firstDayOfCourse}
                    lastDayOfCourse={courseInfo?.lastDayOfCourse}
                    isShowAcademicAdvisorPopup={false}
                    progressData={progressData}
                    attendanceRating={instructional.attendanceRatingByCourse.value?.percentage ?? 0}
                />
            )
        }
        return null
    }, [courseInfo, gpa, progressData, instructional.attendanceRatingByCourse, studentId])

    const getAttendanceStats = () => {
        if (courseId) {
            instructional.getAttendanceRatingByCourse(courseId, studentProfileIds, [])
            getGpaAndLdaInfo()
        }
    }

    const getGpaAndLdaInfo = async () => {
        const params: any = {}
        if (courseId) {
            params.courseId = courseId
            if (courseInfo?.scheduleId) {
                params.scheduleId = courseInfo.scheduleId
            }
        }
        if (courseInfo?.scheduleId) {
            params.scheduleId = courseInfo.scheduleId
        }
        const profileId = get(model, "user.profiles[0].id")
        if (studentId) {
            params.studentProfileId = studentId
        } else if (!model.isStaffOrAdmin() && profileId) {
            params.studentProfileId = profileId
        }
        const {
            termGpa,
            gpa,
            cgpa,
            lda,
            termSapStatus,
            sapStatus,
            termSapDate,
            sapDate,
            academicStudentStatsId,
            academicTermStatsId,
            gradingTemplateId
        } = await academicCourseGradesService.getCourseGradeStudentStats(params)
        setLda(lda)
        setAcademicStudentStatsId(academicStudentStatsId)
        setAcademicTermStatsId(academicTermStatsId)
        setStudentGradingTemplateId(gradingTemplateId)
        setTermGpa(typeof termGpa === "number" ? formatDecimal(termGpa) : termGpa)
        setGpa(typeof gpa === "number" ? formatDecimal(gpa) : gpa)
        setCgpa(typeof cgpa === "number" ? formatDecimal(cgpa) : cgpa)
        setSapStatus(sapStatus)
        setTermSapStatus(termSapStatus)
        setTermSapDate(termSapDate)
        setSapDate(sapDate)
    }

    if (isLoading) {
        return <BaseLoading isShow />
    }

    return (
        <div>
            {courseId ? (
                <>
                    {CourseInfoComponent}
                    <ProfileScore
                        lastDayAttendance={lda}
                        gpa={gpa}
                        cgpa={cgpa}
                        sapStatus={sapStatus}
                        isEditableGpa={model.isStaffOrAdmin()}
                        onEditGpa={popupForEditingGpa.open}
                        sapDate={sapDate}
                        isShowSapStatus
                        isEditableLda={model.isStaffOrAdmin()}
                        isEditableSapStatus={model.isStaffOrAdmin()}
                        onClickEditLda={popupForEditingLda.open}
                        onEditAttendance={popupForEditingAttendance.open}
                        isEditableAttendance={model.isStaffOrAdmin()}
                        onEditSapStatus={popupForEditingSapStatus.open}
                        missedHours={instructional.missedAttendanceStats?.value?.missedHours}
                        attendanceRating={instructional.attendanceRatingByCourse.value?.percentage ?? 0}
                    />
                    <CourseTab
                        getAttendanceStats={getAttendanceStats}
                        courseInfo={courseInfo}
                        scheduleIds={scheduleId ? [scheduleId] : []}
                        studentProfileIds={studentProfileIds}
                        courseIds={[courseId]}
                        studentGradingTemplateId={studentGradingTemplateId}
                        termIds={[]}
                    />
                </>
            ) : (
                <MyCourses isAllTerm history={history} onClickCourseId={onChangeCourseId} studentId={studentId} />
            )}
            <EditLdaPopup
                isShow={popupForEditingLda.isVisible}
                onClose={popupForEditingLda.close}
                courseId={courseId}
                studentProfileId={studentId}
                lda={lda}
                termId={null}
                onRefreshLda={getGpaAndLdaInfo}
                scheduleId={courseInfo?.scheduleId}
            />
            <EditSapStatusPopup
                isShow={popupForEditingSapStatus.isVisible}
                onClose={popupForEditingSapStatus.close}
                courseId={courseId}
                studentProfileId={studentId}
                sapStatus={!courseId ? termSapStatus : sapStatus}
                termId={null}
                sapDate={!courseId ? termSapDate : sapDate}
                onRefreshLda={getGpaAndLdaInfo}
                scheduleId={courseInfo?.scheduleId}
            />
            <EditGpaPopup
                isShow={popupForEditingGpa.isVisible}
                onClose={popupForEditingGpa.close}
                courseId={courseId}
                academicStudentStatsId={academicStudentStatsId}
                academicTermStatsId={academicTermStatsId}
                studentProfileId={studentId}
                gpa={courseId ? gpa : termGpa}
                termId={null}
                sapDate={!courseId ? termSapDate : sapDate}
                onRefreshGpa={() => {
                    getGpaAndLdaInfo()
                    return null
                }}
                scheduleId={courseInfo?.scheduleId}
            />
            <EditAttendancePopup
                isShow={popupForEditingAttendance.isVisible}
                onClose={popupForEditingAttendance.close}
                courseId={courseId}
                academicStudentStatsId={academicStudentStatsId}
                academicTermStatsId={academicTermStatsId}
                studentProfileId={studentId}
                termId={null}
                percentage={instructional.attendanceRatingByCourse.value?.percentage}
                onRefresh={getAttendanceStats}
                scheduleId={courseInfo?.scheduleId}
            />
        </div>
    )
})

export {AcademicsStudentCourses}
